import React, { FC, useEffect, useState } from 'react';
import useCartModelDisplay from '../../hooks/useCartModelDisplay';
import { Divider, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import Cart from '../../@types/Cart';
import { TabContext, TabPanel } from '@mui/lab';
import XSeriesChassisOptions from './XSeriesChassisOptions/XSeriesChassisOptions';
import ESeriesChassisOptions from './ESeriesChassisOptions/ESeriesChassisOptions';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChassisType } from '../../@types/ChassisType';
import CartBuilder from './CartBuilder/CartBuilder';
import { toast } from 'react-toastify';

import * as yup from "yup";
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Shape from '../../@types/Shape';
import Product from '../../@types/Product';
import assets from '../../assets';
import SaveBuildFormModal from './SaveBuildFormModal/SaveBuildFormModal';

// Chassis Assembled
import { X4Assy as X4Assy2024 } from '../../data/products/Carts/XSeriesProducts/X4/X4Chassis-2024';
import { X4Assy as X4Assy2025 } from '../../data/products/Carts/XSeriesProducts/X4/X4Chassis-2025';
import { X6Assy as X6Assy2024 } from '../../data/products/Carts/XSeriesProducts/X6/X6Chassis-2024';
import { X6Assy as X6Assy2025 } from '../../data/products/Carts/XSeriesProducts/X6/X6Chassis-2025';
import { E4Assy as E4Assy2024 } from '../../data/products/Carts/ESeriesProducts/E4/E4Chassis-2024';
import { E4Assy as E4Assy2025 } from '../../data/products/Carts/ESeriesProducts/E4/E4Chassis-2025';

// Chassis
import { X4 as X4_2024 } from '../../data/products/Carts/XSeriesProducts/X4/X4Chassis-2024';
import { X4 as X4_2025 } from '../../data/products/Carts/XSeriesProducts/X4/X4Chassis-2025';
import { X6 as X6_2024 } from '../../data/products/Carts/XSeriesProducts/X6/X6Chassis-2024';
import { X6 as X6_2025 } from '../../data/products/Carts/XSeriesProducts/X6/X6Chassis-2025';
import { E4 as E4_2024 } from '../../data/products/Carts/ESeriesProducts/E4/E4Chassis-2024';
import { E4 as E4_2025 } from '../../data/products/Carts/ESeriesProducts/E4/E4Chassis-2025';
import { allChassis, allChassisAssy } from '../../data/products/Carts/AllCartProducts';
import { DefaultCartModelDisplay } from '../CartModelDisplay/CartModelDisplay';
import useCurrencyConverter from '../../hooks/useCurrencyConverter';
import Select from '../common/Select/Select';
import flatten from 'flat';

interface CartConfiguratorProps {
  retailConfigurator?: boolean;
  preassembled?: boolean;
  defaultValues?: Cart | Partial<Cart>;
  modalWindow?: boolean;
  onSave?: (data: Cart) => void;
  currentCartCount?: number;
  minCartQty?: number;
  maxCartQty?: number;
  hideAllPrices?: boolean;
}

const CartConfigurator: FC<CartConfiguratorProps> = ({
  retailConfigurator = true,
  preassembled = true,
  defaultValues,
  modalWindow,
  onSave,
  currentCartCount = 1,
  minCartQty = 1,
  maxCartQty = 27,
  hideAllPrices = false
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    setChassisType,
    chassisType,
    bodyColor,
    liftKit,
    setDefaults
  } = useCartModelDisplay();
  const [showSaveBuildForm, setShowSaveBuildForm] = useState<boolean>(false);
  const [tab, setTab] = useState<string>('../');
  const fromInventory: boolean = (defaultValues?.isFromInventory ?? false);
  const totalCarts: number = currentCartCount;
  const { currencyOptions, currency, setCurrency } = useCurrencyConverter();
  //TEMP remove hasLicenses to prevent LSV orders Per Erica by setting false
  //const hasLicenses = false; // user?.companyAttributes?.licenses !== undefined && user?.companyAttributes?.licenses?.length > 0;

  /* 
    Handles changing of hidden tabs
    Handles initial configurator settings by chassisType
  */
  const handleTabChange = (event: React.SyntheticEvent, tab: string, chassisType?: ChassisType) => {
    setChassisType(chassisType);
    setTab(tab);
    if (!modalWindow && retailConfigurator) {
      navigate(`${chassisType ? `${chassisType?.model}-${chassisType?.year}` : `${tab}`}`);
    }
  };

  /* 
    Handles directly changing tabs based on url navigation
    and initial configurator settings based on url query params
    must ensure base route to this
  */
  useEffect(() => {
    if (retailConfigurator) {
      reset();
      const pathnames = location.pathname.split("/");
      if (pathnames.includes('X4-2024')) {
        setChassisType(
          preassembled || fromInventory
            ? X4Assy2024
            : X4_2024
        );
        setTab('/cart-builder');
      } else if (pathnames.includes('X4-2025')) {
        setChassisType(
          preassembled || fromInventory
            ? X4Assy2025
            : X4_2025
        );
        setTab('/cart-builder');
      } else if (pathnames.includes('X6-2024')) {
        setChassisType(
          preassembled || fromInventory
            ? X6Assy2024
            : X6_2024
        );
        setTab('/cart-builder');
      } else if (pathnames.includes('X6-2025')) {
        setChassisType(
          preassembled || fromInventory
            ? X6Assy2025
            : X6_2025
        );
        setTab('/cart-builder');
      } else if (pathnames.includes('E4-2024')) {
        setChassisType(
          preassembled || fromInventory
            ? E4Assy2024
            : E4_2024
        );
        setTab('/cart-builder');
      } else if (pathnames.includes('E4-2025')) {
        setChassisType(
          preassembled || fromInventory
            ? E4Assy2025
            : E4_2025
        );
        setTab('/cart-builder');
      } else {
        setTab('../');
        setChassisType(undefined);
      }
    }
  }, [
    location,
    retailConfigurator,
    setChassisType
  ]);

  const validationSchema = yup.object<Shape<Cart>>({
    model: yup.string().required(),
    year: yup.number().required(),
    chassisType: yup.string().required(),
    chassisTypeSku: yup.string().required('Please select a configuration series'),
    chassisTypePrice: yup.number().required(),
    bodyColor: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    frontSeat: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    middleSeat: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }).optional().default(undefined),
    rearSeat: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }).optional().default(undefined),
    seatFrame: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    liftKit: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    chassisBatteryConfig: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    chassisBattery: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    chassisSku: yup.string().required(),
    wheelSize: yup.number()
      .required('Please select a wheel size')
      .positive('Please select a wheel size')
      .typeError('Please select a wheel size')
      .required('Please select a wheel size')
      .when('liftKitSku',
        {
          is: 'NL',
          then: yup.number().required('Please select a wheel type').test(
            'lifted_and_15_inch',
            'Please select a wheel type',
            (value) => value !== 15
          )
        }
      ),
    wheel: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    tire: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    lsvKit: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }).when([], {
      //TEMP remove hasLicenses to prevent LSV orders Per Erica by setting false
      //is: () => hasLicenses === true,
      is: () => undefined === true,
      then: () => yup.object<Shape<Product>>({
        id: yup.number().required(),
        name: yup.string().required(),
        sku: yup.string().required(),
        price: yup.number().required(),
        retailPrice: yup.number().required()
      }).required('Please select an LSV kit option')
        .typeError('Please select an LSV kit option'),
      otherwise: () => yup.object<Shape<Product>>({
        id: yup.number().required(),
        name: yup.string().required(),
        sku: yup.string().required(),
        price: yup.number().required(),
        retailPrice: yup.number().required()
      }).optional().default(undefined),
    }),
    lugNut: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    top: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    windshield: yup.object<Shape<Product>>({
      id: yup.number().required(),
      name: yup.string().required(),
      sku: yup.string().required(),
      price: yup.number().required(),
      retailPrice: yup.number().required()
    }),
    includedAccessories: yup.array().of(
      yup.object<Shape<Product>>({
        id: yup.number().required(),
        name: yup.string().required(),
        sku: yup.string().required(),
        price: yup.number().required(),
        retailPrice: yup.number().required()
      })
    ).optional(),
    optionalAccessories: yup.array().of(
      yup.object<Shape<Product>>({
        id: yup.number().required(),
        name: yup.string().required(),
        sku: yup.string().required(),
        price: yup.number().required(),
        retailPrice: yup.number().required()
      })
    ).optional(),
    quantity: yup.number()
      .positive()
      .typeError('Please enter a quantity')
      .required('Please enter a quantity')
      .test('not_exceed_order_27',
        `Total carts must be within ${minCartQty}-${maxCartQty} total`,
        () => totalCarts <= maxCartQty),
    urlBuildParams: yup.string().optional(),
    isFromInventory: yup.boolean().optional(),
    fromInventoryChassisSku: yup.string().optional(),
    cartPrice: yup.number().required(),
    total: yup.number().required()
  });

  const methods = useForm<Cart>({
    mode: 'all',
    defaultValues: defaultValues ?? { quantity: 1 },
    resolver: yupResolver(validationSchema),
    resetOptions: { keepDirty: true, keepErrors: false }
  });

  const { handleSubmit, reset, getValues } = methods;

  const onSubmit = (data: Cart) => {
    if (!retailConfigurator) {
      if (onSave) onSave(data);
    } else {
      setShowSaveBuildForm(true);
    }
  }

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  }

  // Prevent event bubbling up to parent form (submit event)
  const handleSubmitWithoutPropagation = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    handleSubmit(onSubmit, onError)(e);
  }

  /*
    Handles default values for the model based on
    default cart values supplied directly through parent
    auto generated to supply model and show matched model and form selected
    and lastly default cart values supplied from url params
  */
  useEffect(() => {
    if (defaultValues) {
      // Get the chassisType product
      const chassis = [...allChassis, ...allChassisAssy]
        .find((chassis) => chassis.sku === defaultValues.chassisTypeSku);
      if (chassis) {
        const modelDefaults: DefaultCartModelDisplay = {
          chassisType: chassis,
          bodyColor: defaultValues?.bodyColor,
          liftKit: defaultValues?.liftKit,
          wheel: defaultValues?.wheel,
          frontSeat: defaultValues?.frontSeat,
          rearSeat: defaultValues?.rearSeat
        }
        setDefaults(modelDefaults);

        // Set the correct tab
        setTab('/cart-builder');
      }
    } else {

      /* 
        Get available url query params to generate build
          keys: 
          bc = bodyColorSku
          lk = liftKitSku
          fs = frontSeatSku
          wh = wheelSku
          cb = chassisBatteryConfigSku
          oa = optionalAccessoriesSku Array
          optionalAccessories are read from an array comma separated\
          example url: /X6?bc=BL02&lk=L&fs=1&wh=1&cb=27-035&oa=19,21,22,24,28
      */

      const bodyColorSkuUrlParam = searchParams.get('bc');
      const liftKitSkuUrlParam = searchParams.get('lk');
      const frontSeatSkuUrlParam = searchParams.get('fs');
      const wheelSkuUrlParam = searchParams.get('wh');
      const chassisBatteryConfigSkuUrlParam = searchParams.get('cb');
      const optionalAccessoriesSkuUrlParam = searchParams.get('oa');

      /* 
        Use the url params when avaiable if not auto generate a random build
        with the form fields filled out appropriately
      */

      // Variable used to submit to the form
      const generatedCartBuild: any = { quantity: 1 };

      // Battery Chassis Config
      const urlChassisBatteryConfig = chassisType?.chassisBatteryConfigOptions.find((product) => product.sku === chassisBatteryConfigSkuUrlParam);
      if (urlChassisBatteryConfig) {
        generatedCartBuild.chassisBatteryConfig = urlChassisBatteryConfig;
      } else {
        generatedCartBuild.chassisBatteryConfig = chassisType?.chassisBatteryConfigOptions[0]
      }

      // Body Color
      const urlBodyColor = chassisType?.bodyColorOptions.find((product) => product.sku === bodyColorSkuUrlParam);
      if (urlBodyColor) {
        generatedCartBuild.bodyColor = urlBodyColor;
      } else {
        const bodyColorSkusToHide = ['GN06', 'GN07', 'YE02', 'VL03', 'OR05'];
        const bodyColorOptions = chassisType?.bodyColorOptions.filter((product) => !bodyColorSkusToHide.includes(product.sku));
        const bodyColorIndex = getRandomInt(0, bodyColorOptions?.length ? bodyColorOptions?.length - 1 : 0);
        const bodyColor = chassisType?.bodyColorOptions[bodyColorIndex];
        generatedCartBuild.bodyColor = bodyColor;
      }

      // Lift Kit
      const urlLiftKit = chassisType?.liftKitOptions.find((product) => product.sku === liftKitSkuUrlParam);
      if (urlLiftKit) {
        generatedCartBuild.liftKit = urlLiftKit;
      } else {
        const liftKitIndex = getRandomInt(0, chassisType?.liftKitOptions.length ? chassisType?.liftKitOptions.length - 1 : 0);
        const liftKit = chassisType?.liftKitOptions[liftKitIndex];
        generatedCartBuild.liftKit = liftKit;
      }

      // Front Seat
      const urlFrontSeat = chassisType?.frontSeatOptions.find((product) => product.sku === frontSeatSkuUrlParam);
      if (urlFrontSeat) {
        generatedCartBuild.frontSeat = urlFrontSeat;
      } else {
        const frontSeat = chassisType?.frontSeatOptions.find((product) => product.refId === bodyColor?.sku);
        generatedCartBuild.frontSeat = frontSeat;
      }

      // Wheel
      const urlWheel = generatedCartBuild.liftKit
        ? chassisType?.wheelOptionsLifted.find((product) => product.sku === wheelSkuUrlParam)
        : chassisType?.wheelOptions.find((product) => product.sku === wheelSkuUrlParam);

      if (urlWheel) {
        generatedCartBuild.wheel = urlWheel;
      } else {
        const wheelIndex = generatedCartBuild.liftKit?.sku === 'L'
          ? getRandomInt(0, chassisType?.wheelOptionsLifted.length ? chassisType?.wheelOptionsLifted.length - 1 : 0)
          : getRandomInt(0, chassisType?.wheelOptions.length ? chassisType?.wheelOptions.length - 1 : 0);
        const wheel = generatedCartBuild.liftKit?.sku === 'L'
          ? chassisType?.wheelOptionsLifted[wheelIndex]
          : chassisType?.wheelOptions[wheelIndex];
        generatedCartBuild.wheel = wheel;
      }

      // Optional Accessories
      const optionalAccessoriesSkuArrayUrlParams = optionalAccessoriesSkuUrlParam?.split(',');
      const urlOptionalAccessories = liftKit?.sku === 'L'
        ? [
          ...chassisType?.optionalAccessories ?? [],
          ...chassisType?.optionalAccessoriesLifted ?? []
        ]
        : [
          ...chassisType?.optionalAccessories ?? []
        ].filter((product) => optionalAccessoriesSkuArrayUrlParams?.includes(product.sku));
      generatedCartBuild.optionalAccessories = [...urlOptionalAccessories];


      reset(generatedCartBuild, {});
    }
    function getRandomInt(min: number, max: number) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }
  }, [
    defaultValues,
    chassisType,
    retailConfigurator,
    searchParams,
    setTab,
    setChassisType,
    reset
  ]);

  const theme = useTheme();
  
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  return (
    <>
      <TabContext value={tab}>
        {/* {renderCount} */}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            visibility: 'hidden',
            minHeight: 0,
            maxHeight: 0,
          }}>
          <Tab label="home" value="../" />
          <Tab label="cart-configurator-detail" value="/cart-builder" />
        </Tabs>
        <TabPanel sx={{
          backgroundColor: '#FFF',
          padding: '0px 0px 24px 0px',
          overflowY: 'auto'
        }} value="../">
          <Stack
            direction="column"
            alignItems="center"
            marginTop={!retailConfigurator ? 8 : 0}
            marginBottom={8}>
            {retailConfigurator &&
              <RetailConfiguratorHeader />
            }
            <Stack
              direction="column"
              alignItems="center"
              width="100%"
              maxWidth="1400px"
              spacing={8}>
              {retailConfigurator &&
                <Typography
                  letterSpacing={3}
                  fontWeight="bold"
                  sx={(theme) => ({
                    fontSize: 48,
                    textAlign: 'center',
                    [theme.breakpoints.down('tablet')]: {
                      fontSize: 36
                    }
                  })}
                  variant="primaryFont"
                  textTransform="uppercase">
                  Choose A Series
                </Typography>
              }
              {!hideAllPrices &&
                <Select
                  label={'Currency'}
                  value={currency}
                  options={currencyOptions}
                  autoWidth={false}
                  multiple={false}
                  native={false}
                  onChange={(event) => setCurrency(String(event.target.value))} />
              }
              <Stack
                direction="column"
                width="100%"
                spacing={14}
                sx={{
                  '& .MuiDivider-root': {
                    marginLeft: '15px',
                    marginRight: '15px',
                    ':before, :after': {
                      borderColor: '#777777'
                    },
                    '& .MuiDivider-wrapper': {
                      paddingLeft: '24px',
                      paddingRight: '24px'
                    }
                  }
                }}>
                {process.env.REACT_APP_CART_CONFIGURATOR_SHOW_XSERIES === 'true' &&
                  <Stack
                    direction="column"
                    spacing={4}>
                    <Divider flexItem>
                      <img
                        src={`https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/a3def8a2-08db-4ed5-3e52-434b01fd7700/w=${isTablet ? 200 : 250}`}
                        alt="MadJax XSeries Logo" />
                    </Divider>
                    <XSeriesChassisOptions
                      retailConfigurator={retailConfigurator}
                      handleTabChange={handleTabChange}
                      preassembled={preassembled}
                      hideAllPrices={hideAllPrices} />
                  </Stack>
                }
                {process.env.REACT_APP_CART_CONFIGURATOR_SHOW_ESERIES === 'true' &&
                  <Stack
                    direction="column"
                    spacing={4}>
                    <Divider flexItem>
                      <img
                        src={`https://imagedelivery.net/8VO3fVWhjd7OVEhtl_T8-g/bdade445-e4e5-48a2-12c3-633f95eeec00/w=${isTablet ? 200 : 250}`}
                        alt="MadJax ESeries Logo" />
                    </Divider>
                    <ESeriesChassisOptions
                      retailConfigurator={retailConfigurator}
                      handleTabChange={handleTabChange}
                      preassembled={preassembled}
                      hideAllPrices={hideAllPrices} />
                  </Stack>
                }
              </Stack>
            </Stack>
          </Stack>
        </TabPanel>
        <TabPanel sx={{ padding: 0 }} value="/cart-builder">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmitWithoutPropagation}>
              <CartBuilder
                editingCart={defaultValues !== undefined}
                retailConfigurator={retailConfigurator}
                fromInventory={fromInventory}
                hideAllPrices={hideAllPrices} />
            </form>
            {(retailConfigurator && showSaveBuildForm) &&
              <SaveBuildFormModal close={() => setShowSaveBuildForm(false)} cartBuild={getValues()} />
            }
          </FormProvider>
        </TabPanel>
      </TabContext >
    </>
  );
}

const RetailConfiguratorHeader: FC = ({ }) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));
  return (
    <Stack
      direction="column"
      width="100%"
      alignItems="center">
      {/*       <Stack sx={(theme) => ({
        width: '100%',
        height: '80px',
        backgroundColor: '#313538',
        alignItems: 'center',
        justifyContent: 'center',
        [theme.breakpoints.between(1231, 1300)]: {
          height: '103px'
        },
        [theme.breakpoints.between(1069, 1230)]: {
          height: '186px'
        },
        [theme.breakpoints.between(992, 1068)]: {
          height: '208px'
        },
        [theme.breakpoints.between(0, 991)]: {
          height: '77px'
        },
      })}>
        <Typography color="white">MADJAX.COM NAV HEADER HERE</Typography>
      </Stack> */}
      <Stack
        sx={(theme) => ({
          width: '100%',
          minHeight: '450px',
          background: '#e0e0e0 no-repeat center center',
          backgroundSize: 'cover',
          backgroundImage: `url(${assets.images.configuratorHero})`,
          marginBottom: '48px',
          alignItems: 'center',
          position: 'relative',
          ':before': {
            content: '""',
            position: 'absolute',
            display: 'block',
            maxHeight: '450px',
            minHeight: '450px',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundImage: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3))',
            zIndex: 1
          }
        })}>
        <Stack
          direction="column"
          marginTop="auto"
          width="100%"
          maxWidth="1400px"
          paddingBottom="24px">
          <Stack padding="2% 4% 2% 4%" spacing={1} zIndex={2}>
            <img src={assets.images.madjaxVehiclesLogoWhite}
              alt="madjax-vehicles"
              width={isTablet ? '285px' : '360px'} />
            <Typography
              variant="primaryFont"
              letterSpacing={3}
              fontWeight="bold"
              fontSize={isTablet ? '28px' : '36px'}
              color="white"
              textTransform="uppercase">
              Build your own
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        direction="column"
        spacing={2}
        sx={{
          maxWidth: '1440px',
          padding: '2% 4% 2% 4%',
          marginBottom: '36px'
        }}>
        <Typography
          variant="primaryFont"
          textTransform="uppercase"
          fontSize={24}
          letterSpacing={3}
          fontWeight="bold"
          sx={{
            background: "linear-gradient(to right, #931216, #E20B23, #FF8230)",
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
          Get it all with MadJax series carts
        </Typography>
        <Typography
          variant="primaryFont">
          More style, more features, and more fun comes standard with MadJax series carts. That's why every cart comes with a long list of premium features and accessories already included.  Customize and make it yours today.
        </Typography>
      </Stack>
    </Stack >
  );
}


export default CartConfigurator;
